import { useState, useEffect } from 'react';
import axios from 'axios';

const BAR_COLORS = ['#ffffff', '#a0a0a0', '#555555', '#333333', '#1e1e1e'];

const formatPct = v => `${v ?? 0}%`;

function MiniBar({ label, value, max, color = '#fff' }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{label}</span>
        <span style={{ fontSize: '0.8rem', fontWeight: '600', color: 'var(--text-primary)' }}>{value}</span>
      </div>
      <div style={{ height: '6px', background: 'var(--bg-elevated)', borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '3px', transition: 'width 600ms ease' }} />
      </div>
    </div>
  );
}

function DonutChart({ segments }) {
  const total = segments.reduce((a, b) => a + parseFloat(b.value || 0), 0);
  if (total === 0) return <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', padding: '20px' }}>No data yet</div>;

  let cumulative = 0;
  const radius = 60;
  const cx = 80, cy = 80;
  const strokeWidth = 18;

  const arcs = segments.map((seg, i) => {
    const pct = parseFloat(seg.value) / total;
    const startAngle = cumulative * 2 * Math.PI - Math.PI / 2;
    cumulative += pct;
    const endAngle = cumulative * 2 * Math.PI - Math.PI / 2;
    const x1 = cx + radius * Math.cos(startAngle);
    const y1 = cy + radius * Math.sin(startAngle);
    const x2 = cx + radius * Math.cos(endAngle);
    const y2 = cy + radius * Math.sin(endAngle);
    const largeArc = pct > 0.5 ? 1 : 0;
    return { ...seg, path: `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`, pct: (pct * 100).toFixed(1) };
  });

  const COLORS = ['#ffffff', '#a0a0a0', '#f87171', '#60a5fa', '#4ade80', '#facc15'];

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
      <svg width="160" height="160" viewBox="0 0 160 160">
        {arcs.map((arc, i) => (
          <path key={i} d={arc.path} fill="none" stroke={COLORS[i % COLORS.length]} strokeWidth={strokeWidth} strokeLinecap="butt" />
        ))}
        <text x={cx} y={cy - 6} textAnchor="middle" fill="#fff" fontSize="18" fontFamily="Bebas Neue, sans-serif" letterSpacing="1">{total}</text>
        <text x={cx} y={cy + 12} textAnchor="middle" fill="#666" fontSize="9" fontFamily="DM Sans, sans-serif">TOTAL</text>
      </svg>
      <div style={{ flex: 1, minWidth: '120px' }}>
        {arcs.map((arc, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: COLORS[i % COLORS.length], flexShrink: 0 }} />
            <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', flex: 1, textTransform: 'capitalize' }}>{arc.label}</span>
            <span style={{ fontSize: '0.78rem', fontWeight: '600', color: 'var(--text-primary)' }}>{arc.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SparkLine({ data, valueKey, labelKey, color = '#fff' }) {
  if (!data || data.length === 0) return <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '20px 0' }}>No data yet</div>;
  const values = data.map(d => parseFloat(d[valueKey]) || 0);
  const max = Math.max(...values, 1);
  const w = 280, h = 80;
  const points = values.map((v, i) => `${(i / (values.length - 1 || 1)) * w},${h - (v / max) * (h - 10) - 5}`).join(' ');

  return (
    <div>
      <svg width="100%" viewBox={`0 0 ${w} ${h}`} style={{ overflow: 'visible' }}>
        <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {values.map((v, i) => (
          <circle key={i} cx={(i / (values.length - 1 || 1)) * w} cy={h - (v / max) * (h - 10) - 5} r="3" fill={color} />
        ))}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
        {data.map((d, i) => (
          <span key={i} style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textAlign: 'center' }}>{d[labelKey]}</span>
        ))}
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/admin/analytics')
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="page"><div className="empty-state"><span className="empty-state-text">Loading analytics...</span></div></div>;
  if (!data) return <div className="page"><div className="empty-state"><span className="empty-state-text">Failed to load analytics.</span></div></div>;

  const { statusBreakdown, instructorStats, bookingsByDay, bookingsByMonth, parentEngagement, completionRate, popularTimes } = data;
  const maxSessions = Math.max(...instructorStats.map(i => parseInt(i.total_sessions) || 0), 1);
  const maxDayCount = Math.max(...bookingsByDay.map(d => parseInt(d.count) || 0), 1);

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">ANALYTICS</h1>
        <p className="page-subtitle">Booking trends, performance metrics, and planning insights</p>
      </div>

      {/* Top KPI row */}
      <div className="stats-grid" style={{ marginBottom: '24px' }}>
        <div className="stat-card">
          <div className="stat-card-label">Completion Rate</div>
          <div className="stat-card-value" style={{ color: '#4ade80' }}>{formatPct(completionRate.completion_rate)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Cancellation Rate</div>
          <div className="stat-card-value" style={{ color: '#f87171' }}>{formatPct(completionRate.cancellation_rate)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">No-Show Rate</div>
          <div className="stat-card-value" style={{ color: '#facc15' }}>{formatPct(completionRate.no_show_rate)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Avg Sessions / Parent</div>
          <div className="stat-card-value">{parentEngagement.avg_sessions_per_parent ?? '—'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Parents Who Booked</div>
          <div className="stat-card-value">{parentEngagement.total_parents_booked ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Most by One Parent</div>
          <div className="stat-card-value">{parentEngagement.max_sessions_by_one_parent ?? 0}</div>
        </div>
      </div>

      {/* Middle row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>

        {/* Session Status Donut */}
        <div className="card">
          <div className="stat-card-label" style={{ marginBottom: '16px' }}>Session Status Breakdown</div>
          <DonutChart segments={statusBreakdown.map(s => ({ label: s.status, value: s.count }))} />
        </div>

        {/* Bookings by Day of Week */}
        <div className="card">
          <div className="stat-card-label" style={{ marginBottom: '16px' }}>Bookings by Day of Week</div>
          {bookingsByDay.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No data yet</div>
          ) : bookingsByDay.map((d, i) => (
            <MiniBar key={i} label={d.day} value={parseInt(d.count)} max={maxDayCount} color={BAR_COLORS[i % BAR_COLORS.length]} />
          ))}
        </div>
      </div>

      {/* Bottom row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>

        {/* Bookings over time */}
        <div className="card">
          <div className="stat-card-label" style={{ marginBottom: '16px' }}>Bookings — Last 6 Months</div>
          <SparkLine data={bookingsByMonth} valueKey="total" labelKey="month" color="#fff" />
          {bookingsByMonth.length > 0 && (
            <div style={{ display: 'flex', gap: '16px', marginTop: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#4ade80' }} /><span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Completed</span></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#f87171' }} /><span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Cancelled</span></div>
            </div>
          )}
        </div>

        {/* Popular time slots */}
        <div className="card">
          <div className="stat-card-label" style={{ marginBottom: '16px' }}>Most Popular Time Slots</div>
          {popularTimes.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No data yet</div>
          ) : popularTimes.map((t, i) => (
            <MiniBar key={i} label={t.time_slot} value={parseInt(t.count)} max={parseInt(popularTimes[0].count)} color={BAR_COLORS[i]} />
          ))}
        </div>
      </div>

      {/* Instructor breakdown */}
      <div className="card">
        <div className="stat-card-label" style={{ marginBottom: '16px' }}>Instructor Performance</div>
        {instructorStats.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No instructors yet</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Instructor', 'Total', 'Confirmed', 'Completed', 'Cancelled', 'Pending', '% of Bookings'].map(h => (
                  <th key={h} style={{ fontSize: '0.7rem', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {instructorStats.map((inst, i) => (
                <tr key={i}>
                  <td style={{ padding: '12px', fontSize: '0.875rem', color: 'var(--text-primary)', fontWeight: '500', borderBottom: '1px solid var(--border)' }}>{inst.first_name} {inst.last_name}</td>
                  <td style={{ padding: '12px', fontSize: '0.875rem', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)' }}>{inst.total_sessions}</td>
                  <td style={{ padding: '12px', fontSize: '0.875rem', color: '#4ade80', borderBottom: '1px solid var(--border)' }}>{inst.confirmed}</td>
                  <td style={{ padding: '12px', fontSize: '0.875rem', color: '#60a5fa', borderBottom: '1px solid var(--border)' }}>{inst.completed}</td>
                  <td style={{ padding: '12px', fontSize: '0.875rem', color: '#f87171', borderBottom: '1px solid var(--border)' }}>{inst.cancelled}</td>
                  <td style={{ padding: '12px', fontSize: '0.875rem', color: '#facc15', borderBottom: '1px solid var(--border)' }}>{inst.pending}</td>
                  <td style={{ padding: '12px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ flex: 1, height: '6px', background: 'var(--bg-elevated)', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${inst.booking_percentage || 0}%`, background: '#fff', borderRadius: '3px' }} />
                      </div>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-primary)', fontWeight: '600', minWidth: '36px' }}>{inst.booking_percentage || 0}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
